import { API_ROUTES } from "@/constants/api";
import { QUERY_KEYS } from "@/constants/config";
import { apiGet, apiPost } from "@/lib/api-client";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { InvoiceRow, InvoiceShareLinks, InvoiceWithOrder, Paginated } from "@bizy/core";

export interface InvoiceListParams {
  page?: number;
  pageSize?: number;
  orderId?: string;
  kind?: string;
}

const toQuery = (params: InvoiceListParams): string => {
  const search = new URLSearchParams();
  if (params.page) search.set("page", String(params.page));
  if (params.pageSize) search.set("pageSize", String(params.pageSize));
  if (params.orderId) search.set("order_id", params.orderId);
  if (params.kind) search.set("kind", params.kind);
  const query = search.toString();
  return query.length > 0 ? `?${query}` : "";
};

export const useInvoices = (workspaceId: string | undefined, params: InvoiceListParams = {}) =>
  useQuery({
    queryKey: [QUERY_KEYS.invoices, workspaceId ?? "none", params],
    enabled: Boolean(workspaceId),
    queryFn: () =>
      apiGet<Paginated<InvoiceRow>>(
        `${API_ROUTES.invoices(workspaceId as string)}${toQuery(params)}`,
      ),
  });

export const useInvoice = (workspaceId: string | undefined, invoiceId: string | undefined) =>
  useQuery({
    queryKey: [QUERY_KEYS.invoice, workspaceId ?? "none", invoiceId ?? "none"],
    enabled: Boolean(workspaceId && invoiceId),
    queryFn: () =>
      apiGet<InvoiceWithOrder>(API_ROUTES.invoice(workspaceId as string, invoiceId as string)),
  });

export interface RegisterAssetPayload {
  invoiceId: string;
  assetKey: string;
  mime: string;
  bytes: number;
  pageSize: string;
  format: string;
}

export const useRegisterInvoiceAsset = (workspaceId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: RegisterAssetPayload) =>
      apiPost<InvoiceRow>(`${API_ROUTES.invoice(workspaceId, payload.invoiceId)}/asset`, {
        asset_key: payload.assetKey,
        mime: payload.mime,
        bytes: payload.bytes,
        page_size: payload.pageSize,
        format: payload.format,
      }),
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.invoice, workspaceId, variables.invoiceId],
      });
      void queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.invoices, workspaceId] });
      void queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.logs, workspaceId] });
    },
  });
};

export const useShareInvoice = (workspaceId: string) =>
  useMutation({
    mutationFn: (input: { invoiceId: string; channel: "whatsapp" | "email"; locale: "en" | "id" }) =>
      apiPost<InvoiceShareLinks>(`${API_ROUTES.invoice(workspaceId, input.invoiceId)}/share`, {
        channel: input.channel,
        locale: input.locale,
      }),
  });

export const useVoidInvoice = (workspaceId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { invoiceId: string; reason?: string }) =>
      apiPost<InvoiceRow>(API_ROUTES.invoiceVoid(workspaceId, input.invoiceId), {
        reason: input.reason,
      }),
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.invoice, workspaceId, variables.invoiceId],
      });
      void queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.invoices, workspaceId] });
    },
  });
};
